// Environment detection utilities for Verfi proof service

export type Environment = 'local' | 'docker' | 'staging' | 'production';

export interface EnvironmentConfig {
  environment: Environment;
  apiUrl: string;
  proofUrl: string;
  wsUrl?: string;
}

/**
 * Detects the current environment based on environment variables (industry best practice)
 * Requires REACT_APP_VERFI_ENV to be explicitly set (fail-hard approach)
 */
export function detectEnvironment(): Environment {
  // Require explicit environment variable (fail-hard approach)
  if (!process.env.REACT_APP_VERFI_ENV) {
    console.error('❌ REACT_APP_VERFI_ENV environment variable is missing!');
    console.error('Available env vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));
    throw new Error('REACT_APP_VERFI_ENV environment variable is required. Set to: local, docker, staging, or production');
  }

  const env = process.env.REACT_APP_VERFI_ENV.toLowerCase();
  console.log('🔍 Detected environment:', env);
  
  if (env === 'production') {
    return 'production';
  }
  if (env === 'staging') {
    return 'staging';
  }
  if (env === 'docker') {
    return 'docker';
  }
  if (env === 'local') {
    return 'local';
  }

  console.error('❌ Invalid REACT_APP_VERFI_ENV value:', env);
  throw new Error(`Invalid REACT_APP_VERFI_ENV value: "${env}". Must be one of: local, docker, staging, production`);
}

/**
 * Gets the appropriate API URL for the current environment
 * Uses environment variables as primary source (industry standard)
 */
export function getApiUrl(): string {
  // Require explicit API URL from environment variable (fail-hard approach)
  if (!process.env.REACT_APP_API_URL) {
    throw new Error('REACT_APP_API_URL environment variable is required. Check environment configuration.');
  }
  
  const environment = detectEnvironment();
  const baseUrl = process.env.REACT_APP_API_URL;
  
  // Production uses no /api prefix, all other environments use /api prefix
  if (environment === 'production') {
    return baseUrl;
  }
  
  return `${baseUrl}/api`;
}

/**
 * Gets the Proof service URL for the current environment
 */
export function getProofUrl(): string {
  // Require explicit Proof URL from environment variable (fail-hard approach)
  if (!process.env.REACT_APP_PROOF_URL) {
    throw new Error('REACT_APP_PROOF_URL environment variable is required. Check environment configuration.');
  }
  
  return process.env.REACT_APP_PROOF_URL;
}

/**
 * Gets the WebSocket URL for the current environment
 */
export function getWsUrl(): string {
  const apiUrl = getApiUrl();
  
  // Convert http to ws, https to wss
  return apiUrl.replace(/^http/, 'ws');
}

/**
 * Gets the complete environment configuration
 */
export function getEnvironmentConfig() {
  const environment = detectEnvironment();
  
  return {
    environment,
    apiUrl: getApiUrl(),
    proofUrl: getProofUrl(),
    wsUrl: getWsUrl(),
  };
}

// Helper function for debugging
export function getEnvironmentInfo() {
  const environment = detectEnvironment();
  return {
    environment,
    apiUrl: getApiUrl(),
    proofUrl: getProofUrl(),
    rawApiUrl: process.env.REACT_APP_API_URL,
    rawProofUrl: process.env.REACT_APP_PROOF_URL,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server',
    port: typeof window !== 'undefined' ? window.location.port : 'server'
  };
}

/**
 * Gets the API base URL (without /api suffix) for the current environment
 * Used for legacy compatibility
 */
export function getApiBaseUrl(): string {
  // Require explicit API URL from environment variable (fail-hard approach)
  if (!process.env.REACT_APP_API_URL) {
    throw new Error('REACT_APP_API_URL environment variable is required. Check environment configuration.');
  }
  
  return process.env.REACT_APP_API_URL;
}
